import { useEffect, useState } from "react";
import api from "../lib/api";

const FALLBACK = [
  { id: "f1", section: "", title: "Coastal Residence — Architecture", field: "Architecture", image_data: "https://images.unsplash.com/photo-1772398539093-fc7b4a6b1bfc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBjb2FzdGFsJTIwYXJjaGl0ZWN0dXJlJTIwc3Vuc2V0fGVufDB8fHx8MTc3OTAzNzI3N3ww&ixlib=rb-4.1.0&q=85", description: "Concept-to-occupancy stewardship of a private coastal residence." },
  { id: "f2", section: "", title: "Hospitality Estate — Travel & Tourism", field: "Travel & Tourism", image_data: "https://images.unsplash.com/photo-1544984243-ec57ea16fe25?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjb2FzdGFsJTIwYXJjaGl0ZWN0dXJlJTIwc3Vuc2V0fGVufDB8fHx8MTc3OTAzNzI3N3ww&ixlib=rb-4.1.0&q=85", description: "Brand & capital strategy for a private island resort." },
  { id: "f3", section: "", title: "Cliffside Villa — Real Estate", field: "Real Estate", image_data: "https://static.prod-images.emergentagent.com/jobs/3a30fb0f-b7cc-433f-9f20-4e03192e39b7/images/5fc2ec50ca2d638d71c9426b6cbd0d268205631e0125477ec5a95e3a929dbb3f.png", description: "Site thesis, financing and design oversight for a flagship villa." },
  { id: "f4", section: "", title: "Sovereign Tower — Government Policy", field: "Government Policy", image_data: "https://static.prod-images.emergentagent.com/jobs/3a30fb0f-b7cc-433f-9f20-4e03192e39b7/images/6cf3bb5360b8a1bf63e06e626a6db46ff0b77141d2d771323144f941626df749.png", description: "Public-private framework for a coastal innovation district." },
];

function ItemCard({ p, featured }) {
  return (
    <article className={`relative group overflow-hidden rounded-sm glass ${featured ? "md:col-span-2 md:row-span-2" : ""}`} data-testid={`portfolio-item-${p.id}`}>
      <div className={`relative ${featured ? "aspect-[16/12] md:aspect-[16/13]" : "aspect-[4/3]"} overflow-hidden`}>
        <img src={p.image_data} alt={p.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" style={{ objectPosition: p.image_position || "50% 50%" }} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#030712] via-[#030712]/30 to-transparent" />
        <div className="absolute left-5 bottom-5 right-5">
          <p className="overline">{p.field}</p>
          {p.event_date ? <p className="text-[10px] text-white/55 mt-0.5 tracking-wide">{p.event_date}</p> : null}
          <p className="font-display text-lg mt-1">{p.title}</p>
          {p.description ? <p className="text-xs text-white/65 mt-1 max-w-md line-clamp-2">{p.description}</p> : null}
        </div>
      </div>
    </article>
  );
}

function groupBySection(items) {
  const map = new Map();
  for (const item of items) {
    const key = (item.section || "").trim();
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(item);
  }
  return Array.from(map.entries());
}

export default function Portfolio() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/gallery", { params: { gallery: "portfolio" } })
      .then(({ data }) => setItems(data && data.length ? data : FALLBACK))
      .catch(() => setItems(FALLBACK))
      .finally(() => setLoading(false));
  }, []);

  const sections = groupBySection(items);
  const hasNamedSections = sections.some(([k]) => k.length > 0);

  return (
    <section id="portfolio" className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24" data-testid="portfolio-section">
      <div className="grid grid-cols-12 gap-6 items-end mb-12">
        <div className="col-span-12 md:col-span-7">
          <p className="overline">Selected Work</p>
          <h2 className="font-display font-light text-4xl lg:text-5xl tracking-tight mt-4">
            Delivered with momentum, <br />
            <span className="italic text-[var(--pov-cyan)]">built to compound.</span>
          </h2>
        </div>
        <div className="col-span-12 md:col-span-5 text-white/60 text-sm md:text-right">
          A representative slice of recent engagements — many of our biggest moves remain off the
          record for now, and that's the point.
        </div>
      </div>

      {loading ? (
        <div className="text-white/50 text-sm" data-testid="portfolio-loading">Loading work…</div>
      ) : hasNamedSections ? (
        <div className="space-y-16" data-testid="portfolio-grouped">
          {sections.map(([sectionName, sectionItems]) => (
            <div key={sectionName || "__none"} data-testid={`portfolio-section-${sectionName || "unsorted"}`}>
              {sectionName ? (
                <div className="mb-6 flex items-baseline justify-between gap-4">
                  <h3 className="font-display text-2xl tracking-tight">{sectionName}</h3>
                  <div className="hairline flex-1 ml-4 mt-3" />
                </div>
              ) : null}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {sectionItems.map((p, i) => (
                  <ItemCard key={p.id} p={p} featured={!sectionName && i === 0} />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="portfolio-grid">
          {items.map((p, i) => <ItemCard key={p.id} p={p} featured={i === 0} />)}
        </div>
      )}
    </section>
  );
}
